/**
 * The Bookings page's behavior half: upcoming from the dashboard read (the app's
 * Bookings tab reads the same), past from the paged history read. Draws nothing.
 */
import { useMemo } from 'react';
import { formatCurrency } from '@/components/payment/formatCurrency';
import { formatCardTime, getBookingCardPill } from '@/pages/BookingHubPage/homeFormat';
import type { BookingPaymentSummary } from '@telify/shared/browser';
import type { PastBookingListItem, UpcomingBooking } from '@/types/dashboard';
import { resolveBookTab } from '@/utils/bookTarget';

import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { useWebDashboard, useWebPastBookings } from '../../hooks/useWebAccount';
import { usePageMover } from '../../pageMover';
import { bookingPhoto } from '../../ui/Photo';
import { contract, type BookingsListActions, type BookingsListEvents, type BookingsListRow, type BookingsListState } from './contract';

export type BookingsListRuntime = PieceRuntime<typeof contract, BookingsListState, BookingsListActions>;

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' });

function dateCells(iso: string, upcoming: boolean) {
  const at = new Date(iso);
  const today = upcoming && at.toDateString() === new Date().toDateString();
  return { month: MONTH.format(at), day: String(at.getDate()), weekday: today ? 'Today' : WEEKDAY.format(at), today, time: keepTimes(formatCardTime(iso)) };
}

/** The Payment column (signed-in-web.html): the amount and what it is; null when the read does not say. */
function paymentCell(p: BookingPaymentSummary | null | undefined): BookingsListRow['payment'] {
  if (!p) return null;
  if (p.state === 'free') return { amount: 'Free', note: null };
  if (p.state === 'refunded') {
    // `amount` is what was collected, `refundedAmount` what came back: the cell shows what stayed paid.
    const collected = p.amount ?? 0;
    const refunded = p.refundedAmount ?? collected;
    return { amount: formatCurrency(Math.max(0, collected - refunded)), note: `${formatCurrency(refunded)} refunded` };
  }
  const note = { paid: 'Paid', 'due-at-session': 'Due at the session', 'due-to-confirm': 'Due to confirm', 'after-approval': 'Collected after approval' }[p.state];
  return { amount: p.amount != null ? formatCurrency(p.amount) : null, note };
}

function upcomingRow(b: UpcomingBooking): BookingsListRow {
  const event = b.service.kind === 'event';
  const going = event && b.capacity != null && b.spotsRemaining != null ? Math.max(0, b.capacity - b.spotsRemaining) : null;
  const loc = b.location;
  const town = [loc.city, loc.state].filter(Boolean).join(', ') || null;
  return {
    id: b.id,
    href: `/account/bookings/${encodeURIComponent(b.id)}`,
    ...dateCells(b.scheduledAt, true),
    name: b.service.name,
    kicker: event ? 'Group event' : null,
    withName: b.provider?.name ? `with ${b.provider.name}` : null,
    photoUrl: b.service.imageUrl,
    photo: bookingPhoto(b.service),
    length: `${b.duration} min`,
    place: loc.isByPhone
      ? { online: false, byPhone: true, main: 'By phone', sub: null }
      : loc.isVirtual
        ? { online: true, byPhone: false, main: 'Online', sub: null }
        : { online: false, byPhone: false, main: loc.name ?? 'In person', sub: going != null ? `${going} going` : town },
    status: getBookingCardPill(b).label,
    tone: 'live',
    payment: paymentCell(b.payment),
  };
}

const PAST_PLACE: Record<PastBookingListItem['locationType'], string> = { virtual: 'Online', phone: 'By phone', in_person: 'In person' };

function pastRow(p: PastBookingListItem): BookingsListRow {
  const cancelled = p.status === 'CANCELLED';
  return {
    id: p.id,
    href: `/account/bookings/${encodeURIComponent(p.id)}`,
    ...dateCells(p.scheduledAt, false),
    name: p.serviceName,
    kicker: null,
    withName: p.providerName ? `with ${p.providerName}` : null,
    photoUrl: null,
    photo: null,
    length: p.durationMinutes ? `${p.durationMinutes} min` : null,
    place: { online: p.locationType === 'virtual', byPhone: p.locationType === 'phone', main: PAST_PLACE[p.locationType], sub: null },
    status: cancelled ? 'Cancelled' : 'Completed',
    tone: cancelled ? 'off' : 'done',
    payment: paymentCell(p.payment),
  };
}

export function useBookingsList(input: PieceInput<BookingsListEvents> = {}): BookingsListRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const dashboard = useWebDashboard();
  const past = useWebPastBookings();
  const upcoming = dashboard.data?.upcoming;
  const pages = past.data?.pages;

  const upcomingRows = useMemo(() => (upcoming ?? []).map(upcomingRow), [upcoming]);
  const pastRows = useMemo(() => (pages ?? []).flatMap((p) => p.items).map(pastRow), [pages]);
  const { fetchNextPage } = past;
  const { refetch: refetchUpcoming } = dashboard;
  const actions = useMemo<BookingsListActions>(
    () => ({ loadMorePast: () => void fetchNextPage(), retryUpcoming: () => void refetchUpcoming(), open: (href) => navigate(href) }),
    [fetchNextPage, refetchUpcoming, navigate],
  );
  // A failed upcoming read still draws the page: it says so with Try again, and the history shows (Codex review 2026-10-26).
  const upcomingFailed = !dashboard.data && dashboard.isError;

  return {
    state: {
      status: dashboard.data || upcomingFailed ? 'ready' : 'loading',
      upcomingStatus: upcomingFailed ? 'error' : 'ready',
      upcoming: upcomingRows,
      past: pastRows,
      pastStatus: past.isError ? 'error' : past.data ? 'ready' : 'loading',
      hasMorePast: Boolean(past.hasNextPage),
      loadingMorePast: past.isFetchingNextPage,
      // Book opens the tab the studio has something on (resolveBookTab, Decision C).
      bookHref: dashboard.data && resolveBookTab(dashboard.data.availability) === 'events' ? '/events' : '/services',
    },
    actions,
    options,
  };
}
